/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components\providers\azureVoiceData.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the AI Integration module.
────────────────────────────────────────────────────────────────*/


import type { AzureTTSVoice } from '../../types';

// Locale code to language name mapping
export const LOCALE_TO_LANGUAGE: Record<string, string> = {
    'af': 'Afrikaans', 'am': 'Amharic', 'ar': 'Arabic', 'az': 'Azerbaijani',
    'bg': 'Bulgarian', 'bn': 'Bengali', 'bs': 'Bosnian', 'ca': 'Catalan',
    'cs': 'Czech', 'cy': 'Welsh', 'da': 'Danish', 'de': 'German',
    'el': 'Greek', 'en': 'English', 'es': 'Spanish', 'et': 'Estonian',
    'eu': 'Basque', 'fa': 'Persian', 'fi': 'Finnish', 'fil': 'Filipino',
    'fr': 'French', 'ga': 'Irish', 'gl': 'Galician', 'gu': 'Gujarati',
    'he': 'Hebrew', 'hi': 'Hindi', 'hr': 'Croatian', 'hu': 'Hungarian',
    'hy': 'Armenian', 'id': 'Indonesian', 'is': 'Icelandic', 'it': 'Italian',
    'ja': 'Japanese', 'jv': 'Javanese', 'ka': 'Georgian', 'kk': 'Kazakh',
    'km': 'Khmer', 'kn': 'Kannada', 'ko': 'Korean', 'lo': 'Lao',
    'lt': 'Lithuanian', 'lv': 'Latvian', 'mk': 'Macedonian', 'ml': 'Malayalam',
    'mn': 'Mongolian', 'mr': 'Marathi', 'ms': 'Malay', 'mt': 'Maltese',
    'my': 'Myanmar', 'nb': 'Norwegian', 'ne': 'Nepali', 'nl': 'Dutch',
    'pa': 'Punjabi', 'pl': 'Polish', 'ps': 'Pashto', 'pt': 'Portuguese',
    'ro': 'Romanian', 'ru': 'Russian', 'si': 'Sinhala', 'sk': 'Slovak',
    'sl': 'Slovenian', 'so': 'Somali', 'sq': 'Albanian', 'sr': 'Serbian',
    'su': 'Sundanese', 'sv': 'Swedish', 'sw': 'Swahili', 'ta': 'Tamil',
    'te': 'Telugu', 'th': 'Thai', 'tr': 'Turkish', 'uk': 'Ukrainian',
    'ur': 'Urdu', 'uz': 'Uzbek', 'vi': 'Vietnamese', 'wuu': 'Wu Chinese',
    'yue': 'Cantonese', 'zh': 'Chinese', 'zu': 'Zulu',
};

// Region/country code to name mapping
export const REGION_TO_NAME: Record<string, string> = {
    'ZA': 'South Africa', 'ET': 'Ethiopia', 'AE': 'UAE', 'BH': 'Bahrain',
    'DZ': 'Algeria', 'EG': 'Egypt', 'IQ': 'Iraq', 'JO': 'Jordan',
    'KW': 'Kuwait', 'LB': 'Lebanon', 'LY': 'Libya', 'MA': 'Morocco',
    'OM': 'Oman', 'QA': 'Qatar', 'SA': 'Saudi Arabia', 'SY': 'Syria',
    'TN': 'Tunisia', 'YE': 'Yemen', 'AT': 'Austria', 'CH': 'Switzerland',
    'DE': 'Germany', 'AU': 'Australia', 'CA': 'Canada', 'GB': 'United Kingdom',
    'GH': 'Ghana', 'HK': 'Hong Kong', 'IE': 'Ireland', 'IN': 'India',
    'KE': 'Kenya', 'NG': 'Nigeria', 'NZ': 'New Zealand', 'PH': 'Philippines',
    'SG': 'Singapore', 'TZ': 'Tanzania', 'US': 'United States', 'AR': 'Argentina',
    'BO': 'Bolivia', 'CL': 'Chile', 'CO': 'Colombia', 'CR': 'Costa Rica',
    'CU': 'Cuba', 'DO': 'Dominican Republic', 'EC': 'Ecuador', 'ES': 'Spain',
    'GQ': 'Equatorial Guinea', 'GT': 'Guatemala', 'HN': 'Honduras', 'MX': 'Mexico',
    'NI': 'Nicaragua', 'PA': 'Panama', 'PE': 'Peru', 'PR': 'Puerto Rico',
    'PY': 'Paraguay', 'SV': 'El Salvador', 'UY': 'Uruguay', 'VE': 'Venezuela',
    'BE': 'Belgium', 'FR': 'France', 'IT': 'Italy', 'PT': 'Portugal',
    'BR': 'Brazil', 'RU': 'Russia', 'CN': 'China', 'TW': 'Taiwan',
    'JP': 'Japan', 'KR': 'Korea', 'PK': 'Pakistan', 'BD': 'Bangladesh',
    'NL': 'Netherlands', 'PL': 'Poland', 'SE': 'Sweden', 'NO': 'Norway',
    'DK': 'Denmark', 'FI': 'Finland', 'CZ': 'Czech Republic', 'SK': 'Slovakia',
    'HU': 'Hungary', 'RO': 'Romania', 'BG': 'Bulgaria', 'GR': 'Greece',
    'TR': 'Turkey', 'IL': 'Israel', 'TH': 'Thailand', 'VN': 'Vietnam',
    'ID': 'Indonesia', 'MY': 'Malaysia', 'KZ': 'Kazakhstan', 'UA': 'Ukraine',
};

/** Extract language name from locale code (e.g., "es-MX" → "Spanish") */
export function getLanguageFromLocale(locale: string): string {
    const langCode = locale.split('-')[0].toLowerCase();
    return LOCALE_TO_LANGUAGE[langCode] || locale;
}

/** Get region name from locale code (e.g., "es-MX" → "Mexico") */
export function getRegionFromLocale(locale: string): string {
    const parts = locale.split('-');
    if (parts.length > 1) {
        const regionCode = parts[1].toUpperCase();
        return REGION_TO_NAME[regionCode] || regionCode;
    }
    return locale;
}

/** Extract sorted unique language names from voice list */
export function extractUniqueLanguages(voices: AzureTTSVoice[]): string[] {
    const languages = new Set<string>();
    voices.forEach(voice => {
        const lang = getLanguageFromLocale(voice.Locale);
        if (lang) languages.add(lang);
    });
    return Array.from(languages).sort();
}

/** Extract locale options for a given language */
export function extractLocalesForLanguage(
    voices: AzureTTSVoice[],
    language: string,
): Array<{ value: string; label: string }> {
    const locales = new Map<string, string>();
    voices.forEach(voice => {
        const lang = getLanguageFromLocale(voice.Locale);
        if (lang === language && !locales.has(voice.Locale)) {
            locales.set(voice.Locale, getRegionFromLocale(voice.Locale));
        }
    });
    return Array.from(locales.entries())
        .map(([value, label]) => ({ value, label: `${label} (${value})` }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

/** Extract voices for a given language and locale */
export function extractVoicesForLocale(
    voices: AzureTTSVoice[],
    language: string,
    locale: string,
): AzureTTSVoice[] {
    return voices
        .filter(v => getLanguageFromLocale(v.Locale) === language && v.Locale === locale)
        .sort((a, b) => a.ShortName.localeCompare(b.ShortName));
}
